import React, { useState, useEffect } from 'react';
import { Camera, FolderOpen, X, Upload as UploadIcon, Plus, Image as ImageIcon, Eye, EyeOff, Trash2, UserCircle, AlertTriangle, Clock } from 'lucide-react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { storage, db, auth } from '../firebase';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import './Upload.css';
import DashboardSidebar from './DashboardSidebar';
import { useNavigate } from "react-router-dom";
import { signOut } from 'firebase/auth';
import imageCompression from 'browser-image-compression';

// ✨ NEW: Maximum photos limit across all albums
const MAX_TOTAL_PHOTOS = 40;
// ✨ NEW: Minimum photos required for portfolio visibility
const MIN_PHOTOS_FOR_VISIBILITY = 5;

// Default cover images to suggest to users
const defaultCoverImages = [
  'https://images.pexels.com/photos/2387793/pexels-photo-2387793.jpeg',
  'https://images.pexels.com/photos/2011824/pexels-photo-2011824.jpeg',
  'https://images.unsplash.com/photo-1750599248900-defdbb60c2b2?q=80&w=1332&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1596387451750-f7bfb51461ef?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1624261315481-15026dba3d29?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
];

const Upload = () => {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedAlbum, setSelectedAlbum] = useState('');
  const [customAlbumName, setCustomAlbumName] = useState('');
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [userAlbums, setUserAlbums] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState({});
  const [viewingAlbum, setViewingAlbum] = useState(null);
  const [albumImages, setAlbumImages] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Profile photo specific states - ✨ UPDATED: Never auto-set from Google
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profileUploading, setProfileUploading] = useState(false);
  const [profileProgress, setProfileProgress] = useState(0);
  const [showProfileUpload, setShowProfileUpload] = useState(false);

  // Cover Image States
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [showCoverUpload, setShowCoverUpload] = useState(false);

  // Photographer data state
  const [photographerData, setPhotographerData] = useState(null);
  const [photographerName, setPhotographerName] = useState('Photographer');

  // ✨ NEW: Album deletion states
  const [deletingAlbum, setDeletingAlbum] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [albumToDelete, setAlbumToDelete] = useState(null);

  // ✨ UPDATED: Simplified consent form states with Firebase tracking
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentTimestamp, setConsentTimestamp] = useState(null);

  // ✨ NEW: Portfolio visibility state
  const [portfolioVisible, setPortfolioVisible] = useState(false);

  // Silent image compression function
  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.4,
      maxWidthOrHeight: 4096,
      useWebWorker: true,
      initialQuality: 0.8,
      fileType: 'image/jpeg',
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      return file;
    }
  };

  // Cover image compression
  const compressCoverImage = async (file) => {
    const options = {
      maxSizeMB: 1.0,
      maxWidthOrHeight: 4096,
      useWebWorker: true,
      initialQuality: 0.9,
      fileType: 'image/jpeg',
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      return file;
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && !sidebarVisible) {
        setSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarVisible]);

  // Get current user and load albums
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        await loadUserAlbums(user.uid);
        await loadProfilePhoto(user.uid);
        await loadCoverImage(user.uid);
        await loadPhotographerData(user.uid);
        await loadConsentStatus(user.uid);
      } else {
        navigate('/Login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // ✨ NEW: Load consent status from Firebase
  const loadConsentStatus = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const hasConsent = userData.uploadConsentAccepted || false;
        const timestamp = userData.uploadConsentTimestamp || null;
        
        setConsentAccepted(hasConsent);
        setConsentTimestamp(timestamp);
        
        console.log('Consent loaded:', { hasConsent, timestamp });
      }
    } catch (error) {
      console.error('Error loading consent status:', error);
    }
  };

  // ✨ NEW: Save consent acceptance to Firebase
  const saveConsentToFirebase = async () => {
    if (!currentUser) return;

    try {
      const timestamp = new Date();
      const userRef = doc(db, 'photographers', currentUser.uid);
      
      await updateDoc(userRef, {
        uploadConsentAccepted: true,
        uploadConsentTimestamp: timestamp,
        uploadConsentVersion: '1.0',
        uploadConsentIP: 'user-device',
      });

      setConsentTimestamp(timestamp);
      console.log('Consent saved to Firebase:', timestamp);
      
    } catch (error) {
      console.error('Error saving consent to Firebase:', error);
      toast.error('Failed to save consent. Please try again.');
      throw error;
    }
  };

  // ✨ UPDATED: Load photographer data with portfolio visibility check
  const loadPhotographerData = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setPhotographerData(userData);
        setPhotographerName(userData.name || userData.displayName || 'Photographer');
        
        // ✨ NEW: Check portfolio visibility status
        const photoCount = userData.portfolioImages?.length || 0;
        const isVisible = photoCount >= MIN_PHOTOS_FOR_VISIBILITY;
        setPortfolioVisible(isVisible);
        
        // ✨ NEW: Update portfolio visibility in Firebase if status changed
        if (userData.portfolioVisible !== isVisible) {
          await updatePortfolioVisibility(userId, isVisible);
        }
      } else {
        if (auth.currentUser) {
          setPhotographerName(auth.currentUser.displayName || 'Photographer');
        }
        setPortfolioVisible(false);
      }
    } catch (error) {
      console.error('Error loading photographer data:', error);
      if (auth.currentUser) {
        setPhotographerName(auth.currentUser.displayName || 'Photographer');
      }
      setPortfolioVisible(false);
    }
  };

  // ✨ NEW: Update portfolio visibility status in Firebase
  const updatePortfolioVisibility = async (userId, isVisible) => {
    try {
      const userRef = doc(db, 'photographers', userId);
      await updateDoc(userRef, {
        portfolioVisible: isVisible,
        portfolioVisibilityUpdated: new Date(),
      });
      console.log(`Portfolio visibility updated: ${isVisible}`);
    } catch (error) {
      console.error('Error updating portfolio visibility:', error);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Logged out successfully');
      navigate('/Login');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  // Handle home navigation
  const handleHomeNavigation = () => {
    navigate('/LandingPage');
    closeSidebar();
  };

  const loadUserAlbums = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const customAlbums = userData.customAlbums || [];
        const defaultAlbums = ['Portfolio'];
        const allAlbums = [...defaultAlbums, ...customAlbums];
        setUserAlbums(allAlbums);
        
        const photoCounts = {};
        const albumData = {};
        for (const album of allAlbums) {
          const albumImages = userData.albums?.[album] || [];
          photoCounts[album] = albumImages.length;
          albumData[album] = {
            count: albumImages.length,
            images: albumImages,
            firstImage: albumImages.length > 0 ? albumImages[0].url : null,
            lastUpdated: albumImages.length > 0 ? albumImages[albumImages.length - 1].uploadedAt : null
          };
        }
        setAlbumPhotos(albumData);
      }
    } catch (error) {
      console.error('Error loading albums:', error);
    }
  };

  // ✨ UPDATED: Load profile photo ONLY from Firebase - NEVER from Google
  const loadProfilePhoto = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const profilePhotoUrl = userData.profileImage || null;
        setProfilePhoto(profilePhotoUrl);
      }
    } catch (error) {
      console.error('Error loading profile photo:', error);
    }
  };

  // Load cover image from Firebase
  const loadCoverImage = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const coverImageUrl = userData.coverImage || null;
        setCoverImage(coverImageUrl);
      }
    } catch (error) {
      console.error('Error loading cover image:', error);
    }
  };

  // Handle profile photo selection with SILENT compression
  const handleProfilePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const isValidType = file.type.startsWith('image/');
      
      if (!isValidType) {
        toast.error('Please select a valid image file');
        return;
      }
      
      try {
        const compressedFile = await compressImage(file);
        setProfilePhotoFile(compressedFile);
        
        const reader = new FileReader();
        reader.onload = (e) => {
          setProfilePhoto(e.target.result);
        };
        reader.readAsDataURL(compressedFile);
        
        toast.success('Profile photo selected. Click upload to save.');
        
      } catch (error) {
        toast.error('Failed to process profile image');
      }
    }
  };

  // Handle cover image selection with SILENT compression
  const handleCoverImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const isValidType = file.type.startsWith('image/');
      
      if (!isValidType) {
        toast.error('Please select a valid image file');
        return;
      }
      
      try {
        const compressedFile = await compressCoverImage(file);
        setCoverImageFile(compressedFile);
        
        const reader = new FileReader();
        reader.onload = (e) => {
          setCoverImage(e.target.result);
        };
        reader.readAsDataURL(compressedFile);
        
        toast.success('Cover image selected. Click upload to save.');
        
      } catch (error) {
        toast.error('Failed to process cover image');
      }
    }
  };

  // Handle default cover image selection
  const handleDefaultCoverSelect = (imageUrl) => {
    setCoverImage(imageUrl);
    setCoverImageFile(null);
    toast.info('Default cover selected. Click upload to save.');
  };

  // Upload profile photo
  const uploadProfilePhoto = async () => {
    if (!profilePhotoFile) {
      toast.error('Please select a profile photo first');
      return;
    }

    setProfileUploading(true);
    setProfileProgress(0);

    try {
      const timestamp = Date.now();
      const fileName = `profile_${timestamp}_${profilePhotoFile.name}`;
      const storageRef = ref(storage, `profile_photos/Photographer/${currentUser.uid}/${fileName}`);
      
      const uploadTask = uploadBytesResumable(storageRef, profilePhotoFile);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProfileProgress(Math.round(progress));
        },
        (error) => {
          console.error('Profile photo upload error:', error);
          toast.error('Failed to upload profile photo');
          setProfileUploading(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            const userRef = doc(db, 'photographers', currentUser.uid);
            await updateDoc(userRef, {
              profileImage: downloadURL,
              profilePhotoLastUpdated: new Date()
            });

            setProfilePhoto(downloadURL);
            setProfilePhotoFile(null);
            setProfileUploading(false);
            setProfileProgress(0);
            setShowProfileUpload(false);
            toast.success('Profile photo uploaded successfully!');
          } catch (error) {
            console.error('Error saving profile photo:', error);
            toast.error('Failed to save profile photo');
            setProfileUploading(false);
          }
        }
      );
    } catch (error) {
      console.error('Profile photo upload error:', error);
      toast.error('Failed to upload profile photo');
      setProfileUploading(false);
    }
  };

  // Upload cover image
  const uploadCoverImage = async () => {
    if (!coverImage) {
      toast.error('Please select a cover image first');
      return;
    }

    setCoverUploading(true);
    setCoverProgress(0);

    try {
      // If there's an existing cover image, delete it first
      if (photographerData?.coverImage && photographerData.coverImage.includes('firebase')) {
        try {
          const oldImageRef = ref(storage, photographerData.coverImage);
          await deleteObject(oldImageRef);
        } catch (deleteError) {
          console.log('Old cover image not found or already deleted');
        }
      }

      let downloadURL = coverImage;

      // If it's a file (not a default image URL), upload it
      if (coverImageFile) {
        const timestamp = Date.now();
        const fileName = `cover_${timestamp}_${coverImageFile.name}`;
        const storageRef = ref(storage, `cover_images/${currentUser.uid}/${fileName}`);
        
        const uploadTask = uploadBytesResumable(storageRef, coverImageFile);
        
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setCoverProgress(Math.round(progress));
            },
            (error) => {
              console.error('Cover image upload error:', error);
              reject(error);
            },
            async () => {
              try {
                downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                resolve();
              } catch (error) {
                reject(error);
              }
            }
          );
        });
      }

      // Save cover image URL to Firestore
      const userRef = doc(db, 'photographers', currentUser.uid);
      await updateDoc(userRef, {
        coverImage: downloadURL,
        coverImageLastUpdated: new Date()
      });

      setCoverImage(downloadURL);
      setCoverImageFile(null);
      setCoverUploading(false);
      setCoverProgress(0);
      setShowCoverUpload(false);
      
      // Update local photographer data
      setPhotographerData(prev => ({
        ...prev,
        coverImage: downloadURL
      }));

      toast.success('Cover image uploaded successfully!');
      
    } catch (error) {
      console.error('Cover image upload error:', error);
      toast.error('Failed to upload cover image');
      setCoverUploading(false);
    }
  };

  // Delete profile photo
  const deleteProfilePhoto = async () => {
    if (!window.confirm('Are you sure you want to delete your profile photo?')) {
      return;
    }

    try {
      if (profilePhoto && profilePhoto.includes('firebase')) {
        const imageRef = ref(storage, profilePhoto);
        await deleteObject(imageRef);
      }

      const userRef = doc(db, 'photographers', currentUser.uid);
      await updateDoc(userRef, {
        profileImage: '',
        profilePhotoLastUpdated: new Date()
      });

      setProfilePhoto(null);
      setProfilePhotoFile(null);
      setShowProfileUpload(false);
      toast.success('Profile photo deleted successfully');
    } catch (error) {
      console.error('Error deleting profile photo:', error);
      toast.error('Failed to delete profile photo');
    }
  };

  // Delete cover image
  const deleteCoverImage = async () => {
    if (!window.confirm('Are you sure you want to delete your cover image?')) {
      return;
    }

    try {
      if (coverImage && coverImage.includes('firebase')) {
        const imageRef = ref(storage, coverImage);
        await deleteObject(imageRef);
      }

      const userRef = doc(db, 'photographers', currentUser.uid);
      await updateDoc(userRef, {
        coverImage: '',
        coverImageLastUpdated: new Date()
      });

      setCoverImage(null);
      setCoverImageFile(null);
      setShowCoverUpload(false);
      
      // Update local photographer data
      setPhotographerData(prev => ({
        ...prev,
        coverImage: ''
      }));

      toast.success('Cover image deleted successfully');
    } catch (error) {
      console.error('Error deleting cover image:', error);
      toast.error('Failed to delete cover image');
    }
  };

  // ✨ NEW: Delete entire album functionality
  const handleDeleteAlbum = (albumName, event) => {
    // Stop event propagation to prevent album click
    event.stopPropagation();
    
    // Prevent deletion of default Portfolio album
    if (albumName === 'Portfolio') {
      toast.error('Cannot delete the Portfolio album');
      return;
    }
    
    setAlbumToDelete(albumName);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteAlbum = async () => {
    if (!albumToDelete || !currentUser) {
      return;
    }

    setDeletingAlbum(albumToDelete);
    setShowDeleteConfirmation(false);

    try {
      // Step 1: Delete all images from Firebase Storage
      const albumStorageRef = ref(storage, `photographers/${currentUser.uid}/albums/${albumToDelete}`);
      
      try {
        const albumFiles = await listAll(albumStorageRef);
        const deletePromises = albumFiles.items.map(fileRef => deleteObject(fileRef));
        await Promise.all(deletePromises);
        console.log(`Deleted ${deletePromises.length} files from storage`);
      } catch (storageError) {
        console.log('No files found in storage or already deleted:', storageError);
      }

      // Step 2: Update photographer document in Firestore
      const userRef = doc(db, 'photographers', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentCustomAlbums = userData.customAlbums || [];
        const currentAlbums = userData.albums || {};
        const currentPortfolioImages = userData.portfolioImages || [];
        
        // Remove album from customAlbums array
        const updatedCustomAlbums = currentCustomAlbums.filter(album => album !== albumToDelete);
        
        // Remove album from albums object
        const updatedAlbums = { ...currentAlbums };
        delete updatedAlbums[albumToDelete];
        
        // Remove images from portfolioImages array that belong to this album
        const updatedPortfolioImages = currentPortfolioImages.filter(
          image => image.albumName !== albumToDelete
        );

        // ✨ NEW: Check if portfolio should become invisible after deletion
        const newPhotoCount = updatedPortfolioImages.length;
        const newVisibility = newPhotoCount >= MIN_PHOTOS_FOR_VISIBILITY;

        // Update the photographer document
        await updateDoc(userRef, {
          customAlbums: updatedCustomAlbums,
          albums: updatedAlbums,
          portfolioImages: updatedPortfolioImages,
          portfolioImageCount: updatedPortfolioImages.length,
          portfolioLastUpdated: new Date(),
          portfolioVisible: newVisibility, // ✨ NEW: Update visibility
          portfolioVisibilityUpdated: new Date()
        });

        // Update local photographer data
        setPhotographerData(prev => ({
          ...prev,
          customAlbums: updatedCustomAlbums,
          albums: updatedAlbums,
          portfolioImages: updatedPortfolioImages,
          portfolioImageCount: updatedPortfolioImages.length
        }));

        // ✨ NEW: Update local visibility state
        setPortfolioVisible(newVisibility);
      }

      // Step 3: Delete album document from albums collection
      try {
        const albumDocId = `${currentUser.uid}_${albumToDelete}`;
        await deleteDoc(doc(db, 'albums', albumDocId));
        console.log('Album document deleted from albums collection');
      } catch (albumDocError) {
        console.log('Album document not found or already deleted:', albumDocError);
      }

      // Step 4: Update local state
      setUserAlbums(prev => prev.filter(album => album !== albumToDelete));
      setAlbumPhotos(prev => {
        const updated = { ...prev };
        delete updated[albumToDelete];
        return updated;
      });

      // If currently viewing this album, go back to main view
      if (viewingAlbum === albumToDelete) {
        setViewingAlbum(null);
        setAlbumImages([]);
      }

      // If this album was selected, clear selection
      if (selectedAlbum === albumToDelete) {
        setSelectedAlbum('');
      }

      const remainingSlots = MAX_TOTAL_PHOTOS - (photographerData?.portfolioImages?.length || 0);
      toast.success(`Album "${albumToDelete}" deleted successfully! You can now upload ${remainingSlots} more photos.`);

      // ✨ NEW: Show visibility warning if portfolio became invisible
      if (!portfolioVisible && getCurrentPhotoCount() < MIN_PHOTOS_FOR_VISIBILITY) {
        toast.warning(`⚠️ Portfolio is now hidden from public view. Upload at least ${MIN_PHOTOS_FOR_VISIBILITY} photos to make it visible again.`);
      }

    } catch (error) {
      console.error('Error deleting album:', error);
      toast.error(`Failed to delete album "${albumToDelete}". Please try again.`);
    } finally {
      setDeletingAlbum(null);
      setAlbumToDelete(null);
    }
  };

  const cancelDeleteAlbum = () => {
    setShowDeleteConfirmation(false);
    setAlbumToDelete(null);
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  // ✨ UPDATED: Handle file selection with consent check and 40-photo limit check
  const handleFileSelect = async (e) => {
    if (!selectedAlbum) {
      toast.error('Please select or create an album first!');
      return;
    }

    if (!consentAccepted) {
      toast.error('Please accept the upload terms first!');
      return;
    }

    const files = Array.from(e.target.files);
    const existingPhotosCount = photographerData?.portfolioImages?.length || 0;
    const totalAfterUpload = existingPhotosCount + files.length;

    // ✨ Check 40-photo limit
    if (totalAfterUpload > MAX_TOTAL_PHOTOS) {
      toast.error(`You can upload a maximum of ${MAX_TOTAL_PHOTOS} photos in total. Currently you have ${existingPhotosCount} photos. Please remove some photos before uploading more.`);
      return;
    }

    await handleFiles(files);
  };

  // ✨ UPDATED: Handle files with consent check and 40-photo limit check
  const handleFiles = async (files) => {
    if (!selectedAlbum) {
      toast.error('Please select or create an album first!');
      return;
    }

    if (!consentAccepted) {
      toast.error('Please accept the upload terms first!');
      return;
    }

    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      
      if (!isValidType) {
        toast.error(`${file.name} is not a valid image file`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // ✨ Double-check the limit before adding to selected files
    const existingPhotosCount = photographerData?.portfolioImages?.length || 0;
    const currentSelectedCount = selectedFiles.length;
    const totalAfterSelection = existingPhotosCount + currentSelectedCount + validFiles.length;

    if (totalAfterSelection > MAX_TOTAL_PHOTOS) {
      const remainingSlots = MAX_TOTAL_PHOTOS - existingPhotosCount - currentSelectedCount;
      toast.error(`You can only add ${remainingSlots} more photos. Total limit is ${MAX_TOTAL_PHOTOS} photos.`);
      return;
    }

    const compressedFiles = [];
    
    for (const file of validFiles) {
      try {
        const compressedFile = await compressImage(file);
        compressedFiles.push(compressedFile);
      } catch (error) {
        compressedFiles.push(file);
      }
    }
    
    if (compressedFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...compressedFiles]);
      const remainingAfterThis = MAX_TOTAL_PHOTOS - existingPhotosCount - currentSelectedCount - compressedFiles.length;
      toast.success(`${compressedFiles.length} file(s) selected for upload to ${selectedAlbum}. You can upload ${remainingAfterThis} more photos.`);
    }
  };

  // ✨ UPDATED: Drag and drop with consent check and 40-photo limit check
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // ✨ UPDATED: Handle drop with consent check and 40-photo limit check
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (!consentAccepted) {
      toast.error('Please accept the upload terms first!');
      return;
    }
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      const existingPhotosCount = photographerData?.portfolioImages?.length || 0;
      const currentSelectedCount = selectedFiles.length;
      const totalAfterDrop = existingPhotosCount + currentSelectedCount + files.length;

      // ✨ Check 40-photo limit
      if (totalAfterDrop > MAX_TOTAL_PHOTOS) {
        const remainingSlots = MAX_TOTAL_PHOTOS - existingPhotosCount - currentSelectedCount;
        toast.error(`You can only add ${remainingSlots} more photos. Total limit is ${MAX_TOTAL_PHOTOS} photos.`);
        return;
      }

      await handleFiles(files);
    }
  };

  // ✨ FIXED: Create custom album with proper error handling and Enter key support
  const createCustomAlbum = async () => {
    if (!customAlbumName.trim()) {
      toast.error('Please enter an album name');
      return;
    }

    if (userAlbums.includes(customAlbumName)) {
      toast.error('Album already exists');
      return;
    }

    if (!currentUser) {
      toast.error('Please log in first');
      return;
    }

    console.log('Creating album:', customAlbumName);
    console.log('Current user:', currentUser?.uid);

    try {
      const userRef = doc(db, 'photographers', currentUser.uid);
      
      // Check if photographer document exists first
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        console.log('Photographer document does not exist. Creating new document...');
        // Create the photographer document if it doesn't exist
        await setDoc(userRef, {
          customAlbums: [customAlbumName],
          albums: {
            [customAlbumName]: []
          },
          portfolioImages: [],
          portfolioImageCount: 0,
          portfolioVisible: false,
          createdAt: new Date(),
          email: currentUser.email,
          uid: currentUser.uid
        });
        console.log('New photographer document created successfully');
      } else {
        console.log('Photographer document exists. Updating...');
        // Update existing document
        await updateDoc(userRef, {
          customAlbums: arrayUnion(customAlbumName),
          [`albums.${customAlbumName}`]: []
        });
        console.log('Photographer document updated successfully');
      }

      // Create album document in albums collection
      const albumDocId = `${currentUser.uid}_${customAlbumName}`;
      await setDoc(doc(db, 'albums', albumDocId), {
        photographerId: currentUser.uid,
        albumName: customAlbumName,
        title: customAlbumName,
        description: `${customAlbumName} photography collection`,
        category: 'Custom',
        createdAt: new Date(),
        coverImage: '',
        location: 'Various locations'
      });
      console.log('Album document created in albums collection');

      // Update local state
      setUserAlbums(prev => [...prev, customAlbumName]);
      setAlbumPhotos(prev => ({ 
        ...prev, 
        [customAlbumName]: { count: 0, images: [], firstImage: null, lastUpdated: null }
      }));
      setSelectedAlbum(customAlbumName);
      setCustomAlbumName('');
      setShowCreateAlbum(false);
      toast.success(`Album "${customAlbumName}" created successfully`);
      
    } catch (error) {
      console.error('Error creating album:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      toast.error(`Failed to create album: ${error.message}`);
    }
  };

  // Upload images (files are already compressed at this point)
  const uploadImages = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    if (!selectedAlbum) {
      toast.error('Please select an album first');
      return;
    }

    if (!currentUser) {
      toast.error('Please log in to upload images');
      return;
    }

    if (!consentAccepted) {
      toast.error('Please accept the upload terms first!');
      return;
    }

    // ✨ Final check before upload
    const existingPhotosCount = photographerData?.portfolioImages?.length || 0;
    const totalAfterUpload = existingPhotosCount + selectedFiles.length;

    if (totalAfterUpload > MAX_TOTAL_PHOTOS) {
      toast.error(`Upload cancelled. This would exceed the ${MAX_TOTAL_PHOTOS} photo limit.`);
      return;
    }

    setUploading(true);
    const uploadPromises = [];
    const newProgress = {};

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileId = `${Date.now()}_${i}`;
      newProgress[fileId] = 0;
      
      const uploadPromise = uploadSingleImage(file, fileId);
      uploadPromises.push(uploadPromise);
    }

    setUploadProgress(newProgress);

    try {
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(result => result.success);
      
      if (successfulUploads.length > 0) {
        await updateUserPortfolio(successfulUploads);
        await updateAlbumsCollection(successfulUploads);
        const newTotal = existingPhotosCount + successfulUploads.length;
        const remainingSlots = MAX_TOTAL_PHOTOS - newTotal;
        
        // ✨ NEW: Check if portfolio became visible after upload
        const wasInvisible = !portfolioVisible;
        const becameVisible = newTotal >= MIN_PHOTOS_FOR_VISIBILITY;
        
        if (wasInvisible && becameVisible) {
          setPortfolioVisible(true);
          toast.success(`🎉 Portfolio is now public! ${successfulUploads.length} image(s) uploaded successfully to ${selectedAlbum}! You can upload ${remainingSlots} more photos.`);
        } else {
          toast.success(`${successfulUploads.length} image(s) uploaded successfully to ${selectedAlbum}! You can upload ${remainingSlots} more photos.`);
        }
        
        setSelectedFiles([]);
        setUploadProgress({});
        await loadUserAlbums(currentUser.uid);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Some uploads failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Upload single image (files are already compressed at this point)
  const uploadSingleImage = async (file, fileId) => {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      
      const storageRef = ref(storage, `photographers/${currentUser.uid}/albums/${selectedAlbum}/${fileName}`);
      
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(prev => ({
              ...prev,
              [fileId]: Math.round(progress)
            }));
          },
          (error) => {
            console.error('Upload error:', error);
            reject({ success: false, error, fileName: file.name });
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                success: true,
                url: downloadURL,
                fileName: file.name,
                albumName: selectedAlbum,
                uploadedAt: new Date(),
                size: file.size,
                type: file.type,
                storagePath: `photographers/${currentUser.uid}/albums/${selectedAlbum}/${fileName}`
              });
            } catch (error) {
              reject({ success: false, error, fileName: file.name });
            }
          }
        );
      });
    } catch (error) {
      return { success: false, error, fileName: file.name };
    }
  };

  // ✨ UPDATED: Update user portfolio with visibility check
  const updateUserPortfolio = async (uploadedImages) => {
    try {
      const userRef = doc(db, 'photographers', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentImages = userData.portfolioImages || [];
        const currentAlbums = userData.albums || {};
        
        const newImages = uploadedImages.map(img => ({
          url: img.url,
          fileName: img.fileName,
          albumName: img.albumName,
          uploadedAt: img.uploadedAt,
          size: img.size,
          type: img.type,
          storagePath: img.storagePath
        }));

        const albumImages = currentAlbums[selectedAlbum] || [];
        const updatedAlbumImages = [...albumImages, ...newImages];
        const updatedPortfolioImages = [...currentImages, ...newImages];

        // ✨ NEW: Check portfolio visibility after upload
        const newPhotoCount = updatedPortfolioImages.length;
        const newVisibility = newPhotoCount >= MIN_PHOTOS_FOR_VISIBILITY;

        await updateDoc(userRef, {
          portfolioImages: updatedPortfolioImages,
          portfolioImageCount: updatedPortfolioImages.length,
          portfolioLastUpdated: new Date(),
          [`albums.${selectedAlbum}`]: updatedAlbumImages,
          portfolioVisible: newVisibility, // ✨ NEW: Update visibility
          portfolioVisibilityUpdated: new Date()
        });

        // Update local photographer data
        setPhotographerData(prev => ({
          ...prev,
          portfolioImages: updatedPortfolioImages,
          portfolioImageCount: updatedPortfolioImages.length
        }));
      }
    } catch (error) {
      console.error('Error updating portfolio:', error);
      throw error;
    }
  };

  // Update albums collection for PhotographerProfile compatibility
  const updateAlbumsCollection = async (uploadedImages) => {
    try {
      const albumId = `${currentUser.uid}_${selectedAlbum}`;
      const albumRef = doc(db, 'albums', albumId);
      const albumDoc = await getDoc(albumRef);
      
      if (albumDoc.exists()) {
        await updateDoc(albumRef, {
          coverImage: uploadedImages[0].url,
          lastUpdated: new Date()
        });
      } else {
        await setDoc(albumRef, {
          photographerId: currentUser.uid,
          albumName: selectedAlbum,
          title: selectedAlbum,
          description: `${selectedAlbum} photography collection`,
          category: selectedAlbum === 'Portfolio' ? 'Portfolio' : 'General',
          createdAt: new Date(),
          coverImage: uploadedImages[0].url,
          location: 'Professional Shoot'
        });
      }
    } catch (error) {
      console.error('Error updating albums collection:', error);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setUploadProgress({});
  };

  // ✨ UPDATED: View album images - now triggered by album card click
  const viewAlbumImages = async (albumName) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const images = userData.albums?.[albumName] || [];
        setAlbumImages(images);
        setViewingAlbum(albumName);
      }
    } catch (error) {
      console.error('Error loading album images:', error);
      toast.error('Failed to load album images');
    }
  };

  // ✨ NEW: Handle album card click
  const handleAlbumClick = (albumName) => {
    viewAlbumImages(albumName);
  };

  // ✨ UPDATED: Delete image with visibility check
  const deleteImage = async (imageUrl, albumName) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);

      const userRef = doc(db, 'photographers', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const albumImages = userData.albums?.[albumName] || [];
        const updatedImages = albumImages.filter(img => img.url !== imageUrl);
        
        const portfolioImages = userData.portfolioImages || [];
        const updatedPortfolioImages = portfolioImages.filter(img => img.url !== imageUrl);
        
        // ✨ NEW: Check portfolio visibility after deletion
        const newPhotoCount = updatedPortfolioImages.length;
        const newVisibility = newPhotoCount >= MIN_PHOTOS_FOR_VISIBILITY;

        await updateDoc(userRef, {
          [`albums.${albumName}`]: updatedImages,
          portfolioImages: updatedPortfolioImages,
          portfolioImageCount: updatedPortfolioImages.length,
          portfolioVisible: newVisibility, // ✨ NEW: Update visibility
          portfolioVisibilityUpdated: new Date()
        });

        setAlbumImages(updatedImages);
        await loadUserAlbums(currentUser.uid);
        
        // Update photographer data to reflect new count
        setPhotographerData(prev => ({
          ...prev,
          portfolioImages: updatedPortfolioImages
        }));

        // ✨ NEW: Update local visibility state
        setPortfolioVisible(newVisibility);
        
        const remainingSlots = MAX_TOTAL_PHOTOS - updatedPortfolioImages.length;
        
        // ✨ NEW: Show visibility warning if portfolio became invisible
        if (portfolioVisible && !newVisibility) {
          toast.warning(`Image deleted successfully! ⚠️ Portfolio is now hidden from public view. Upload at least ${MIN_PHOTOS_FOR_VISIBILITY} photos to make it visible again. You can upload ${remainingSlots} more photos.`);
        } else {
          toast.success(`Image deleted successfully! You can now upload ${remainingSlots} more photos.`);
        }
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  // ✨ Get current photo count and remaining slots
  const getCurrentPhotoCount = () => {
    return photographerData?.portfolioImages?.length || 0;
  };

  const getRemainingSlots = () => {
    return MAX_TOTAL_PHOTOS - getCurrentPhotoCount();
  };

  // ✨ NEW: Get photos needed for visibility
  const getPhotosNeededForVisibility = () => {
    const currentCount = getCurrentPhotoCount();
    return Math.max(0, MIN_PHOTOS_FOR_VISIBILITY - currentCount);
  };

  // ✨ UPDATED: Simple consent toggle with Firebase save
  const handleConsentToggle = async () => {
    try {
      if (!consentAccepted) {
        await saveConsentToFirebase(); // Save to Firebase first
        setConsentAccepted(true);
        toast.success('Upload terms accepted and saved.');
      } else {
        // If they want to revoke consent
        if (window.confirm('Are you sure you want to revoke your consent? This will prevent you from uploading photos.')) {
          const userRef = doc(db, 'photographers', currentUser.uid);
          await updateDoc(userRef, {
            uploadConsentAccepted: false,
            uploadConsentRevokedAt: new Date()
          });
          setConsentAccepted(false);
          toast.info('Upload consent revoked.');
        }
      }
    } catch (error) {
      toast.error('Failed to update consent. Please try again.');
    }
  };

  return (
    <div className="upload-container">
      <DashboardSidebar
        activeItem="upload"
        visible={sidebarVisible}
        isMobile={isMobile}
        onToggle={toggleSidebar}
        onClose={closeSidebar}
        userName={photographerName}
        profilePhoto={profilePhoto}
        onHome={handleHomeNavigation}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <div className={`upload-main-content ${
        !sidebarVisible && !isMobile ? 'expanded' : ''
      }`}>
        <div className="upload-page-header">
          <div>
            <h1 className="upload-page-title">Your work</h1>
            <p className="upload-page-subtitle">
              Manage the albums and photos clients see on your portfolio.
            </p>
          </div>
        </div>

        {/* Status row — portfolio visibility leads, because it is the one
            thing a photographer actually needs to know at a glance. */}
        <div className="upload-status-row">
          <div className="upload-stat-card">
            <div className="upload-stat-label">
              <Eye />
              Portfolio status
            </div>
            <div className="upload-portfolio-status">
              {portfolioVisible ? (
                <>
                  <span className="upload-portfolio-visible">
                    <span className="upload-live-dot"></span>
                    Live
                  </span>
                  <small>Visible to clients browsing photographers.</small>
                </>
              ) : (
                <>
                  <span className="upload-portfolio-hidden">
                    <EyeOff />
                    Hidden
                  </span>
                  <small>
                    Upload {getPhotosNeededForVisibility()} more photo
                    {getPhotosNeededForVisibility() === 1 ? '' : 's'} to go live.
                  </small>
                </>
              )}
            </div>
          </div>

          <div className="upload-stat-card">
            <div className="upload-stat-label">
              <ImageIcon />
              Photos used
            </div>
            <div className="upload-stat-value">
              {getCurrentPhotoCount()}<span style={{ color: 'var(--up-faint)', fontWeight: 500 }}>/{MAX_TOTAL_PHOTOS}</span>
            </div>
            <div className="upload-limit-bar">
              <div
                className={`upload-limit-fill ${
                  getCurrentPhotoCount() >= MAX_TOTAL_PHOTOS
                    ? 'is-full'
                    : getCurrentPhotoCount() / MAX_TOTAL_PHOTOS >= 0.8
                    ? 'is-near'
                    : ''
                }`}
                style={{ width: `${Math.min(100, (getCurrentPhotoCount() / MAX_TOTAL_PHOTOS) * 100)}%` }}
              ></div>
            </div>
            <p className="upload-stat-meta">{getRemainingSlots()} slots remaining</p>
          </div>

          <div className="upload-stat-card">
            <div className="upload-stat-label">
              <FolderOpen />
              Albums
            </div>
            <div className="upload-stat-value">{userAlbums.length}</div>
            <p className="upload-stat-meta">
              {userAlbums.length === 1 ? 'collection' : 'collections'}
            </p>
          </div>
        </div>

        {getCurrentPhotoCount() >= MAX_TOTAL_PHOTOS && (
          <div className="upload-alert is-danger">
            <AlertTriangle />
            <span>Upload limit reached. Delete some photos before adding more.</span>
          </div>
        )}

        {/* ✨ UPDATED: Minimal Consent Form placed after photo usage */}
        <div className="upload-consent-simple">
          <h3>Upload Terms</h3>
          <div className="upload-consent-items">
            <label className="upload-consent-item">
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={handleConsentToggle}
                className="upload-consent-checkbox"
              />
              <div className="upload-consent-text">
                <span>I confirm that all the photos I have uploaded are my own original work and that I hold full rights to them.</span>
                <span>I confirm that any photos containing clients have been uploaded only with their consent.</span>
              </div>
              {consentTimestamp && (
                <small className="upload-consent-timestamp">
                  Accepted on: {formatDate(consentTimestamp)}
                </small>
              )}
            </label>
          </div>
        </div>
        
        {!viewingAlbum ? (
          <>
            {/* Cover Image Section */}
            <section className="upload-content-section">
              <div className="upload-section-header">
                <h2 className="upload-section-title">Cover Image</h2>
                <button 
                  className="upload-profile-toggle-btn"
                  onClick={() => setShowCoverUpload(!showCoverUpload)}
                >
                  {showCoverUpload ? 'Hide Upload' : 'Upload Cover Image'}
                </button>
              </div>
              
              <div className="upload-profile-display">
                {coverImage ? (
                  <div className="upload-profile-current">
                    <div className="upload-cover-preview">
                      <img
                        src={coverImage}
                        alt="Cover"
                        className="upload-cover-image"
                      />
                      <div className="upload-profile-overlay">
                        <button 
                          className="upload-profile-action-btn"
                          onClick={() => openImageModal({ url: coverImage, fileName: 'Cover Image' })}
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          className="upload-profile-action-btn upload-delete-btn"
                          onClick={deleteCoverImage}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="upload-profile-info">
                      <h3>Current Cover Image</h3>
                      <p>This image appears as your profile header</p>
                    </div>
                  </div>
                ) : (
                  <div className="upload-profile-empty">
                    <ImageIcon size={48}  />
                    <h3>No Cover Image</h3>
                    <p>Upload a cover image or select from suggestions below</p>
                    
                    {/* Default Cover Image Suggestions */}
                    <div className="upload-default-covers">
                      <h4>Suggested Cover Images:</h4>
                      <div className="upload-default-covers-grid">
                        {defaultCoverImages.map((imageUrl, index) => (
                          <div 
                            key={index} 
                            className="upload-default-cover-item"
                            onClick={() => handleDefaultCoverSelect(imageUrl)}
                          >
                            <img
                              src={imageUrl}
                              alt={`Default Cover ${index + 1}`}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Cover Upload Form - Toggleable */}
              {showCoverUpload && (
                <div className="upload-profile-form">
                  <div className="upload-profile-upload-area">
                    <h4>Upload New Cover Image</h4>
                    <div className="upload-profile-actions">
                      <label className="upload-profile-file-label">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoverImageSelect}
                          style={{ display: 'none' }}
                        />
                        <span className="upload-profile-browse-btn">
                          <ImageIcon size={16} />
                          Select Cover Image
                        </span>
                      </label>
                      
                      {(coverImageFile || coverImage) && (
                        <div className="upload-profile-selected">
                          <p>Selected: {coverImageFile ? coverImageFile.name : 'Default cover image'}</p>
                          <button 
                            onClick={uploadCoverImage}
                            disabled={coverUploading}
                            className="upload-profile-upload-btn"
                          >
                            {coverUploading ? (
                              <>
                                <div className="upload-spinner"></div>
                                Uploading... {coverProgress}%
                              </>
                            ) : (
                              <>
                                <UploadIcon size={16} />
                                Upload Cover Image
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {coverUploading && (
                      <div className="upload-profile-progress">
                        <div className="upload-progress-bar">
                          <div 
                            className="upload-progress-fill"
                            style={{ width: `${coverProgress}%` }}
                          ></div>
                        </div>
                        <span className="upload-progress-text">{coverProgress}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Profile Photo Section */}
            <section className="upload-content-section">
              <div className="upload-section-header">
                <h2 className="upload-section-title">Profile Photo</h2>
                <button 
                  className="upload-profile-toggle-btn"
                  onClick={() => setShowProfileUpload(!showProfileUpload)}
                >
                  {showProfileUpload ? 'Hide Upload' : 'Upload Profile Photo'}
                </button>
              </div>
              
              <div className="upload-profile-display">
                {profilePhoto ? (
                  <div className="upload-profile-current">
                    <div className="upload-profile-preview">
                      <img 
                        src={profilePhoto} 
                        alt="Profile"
                        className="upload-profile-image"
                      />
                      <div className="upload-profile-overlay">
                        <button 
                          className="upload-profile-action-btn"
                          onClick={() => openImageModal({ url: profilePhoto, fileName: 'Profile Photo' })}
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          className="upload-profile-action-btn upload-delete-btn"
                          onClick={deleteProfilePhoto}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="upload-profile-info">
                      <h3>Current Profile Photo</h3>
                      <p>This photo represents you as a photographer</p>
                    </div>
                  </div>
                ) : (
                  <div className="upload-profile-empty">
                    <UserCircle size={48}  />
                    <h3>No Profile Photo</h3>
                    <p>Upload a professional photo of yourself</p>
                  </div>
                )}
              </div>

              {/* Profile Upload Form - Toggleable */}
              {showProfileUpload && (
                <div className="upload-profile-form">
                  <div className="upload-profile-upload-area">
                    <h4>Upload New Profile Photo</h4>
                    <div className="upload-profile-actions">
                      <label className="upload-profile-file-label">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePhotoSelect}
                          style={{ display: 'none' }}
                        />
                        <span className="upload-profile-browse-btn">
                          <ImageIcon size={16} />
                          Select Profile Photo
                        </span>
                      </label>
                      
                      {profilePhotoFile && (
                        <div className="upload-profile-selected">
                          <p>Selected: {profilePhotoFile.name}</p>
                          <button 
                            onClick={uploadProfilePhoto}
                            disabled={profileUploading}
                            className="upload-profile-upload-btn"
                          >
                            {profileUploading ? (
                              <>
                                <div className="upload-spinner"></div>
                                Uploading... {profileProgress}%
                              </>
                            ) : (
                              <>
                                <UploadIcon size={16} />
                                Upload Profile Photo
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {profileUploading && (
                      <div className="upload-profile-progress">
                        <div className="upload-progress-bar">
                          <div 
                            className="upload-progress-fill"
                            style={{ width: `${profileProgress}%` }}
                          ></div>
                        </div>
                        <span className="upload-progress-text">{profileProgress}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Album Selection Section - ✨ FIXED WITH BETTER UI */}
            <section className="upload-content-section">
              <div className="upload-section-header">
                <FolderOpen size={20}  />
                <h2 className="upload-section-title">Select or Create Album</h2>
              </div>
              
              <div className="upload-album-selection">
                <div className="upload-album-header">
                  <label>Select Album to Upload Photos:</label>
                  <button 
                    className="upload-create-album-btn"
                    onClick={() => setShowCreateAlbum(!showCreateAlbum)}
                  >
                    <Plus size={16} /> {showCreateAlbum ? 'Cancel' : 'Create New Album'}
                  </button>
                </div>
                
                <select 
                  value={selectedAlbum} 
                  onChange={(e) => setSelectedAlbum(e.target.value)}
                  className="upload-album-select"
                >
                  <option value="">Choose an album...</option>
                  {userAlbums.map(album => (
                    <option key={album} value={album}>{album}</option>
                  ))}
                </select>

                {showCreateAlbum && (
                  <div className="upload-create-album-form">
                    <input
                      type="text"
                      placeholder="Enter album name"
                      value={customAlbumName}
                      onChange={(e) => setCustomAlbumName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && customAlbumName.trim()) {
                          createCustomAlbum();
                        }
                      }}
                      className="upload-album-input"
                      autoFocus
                    />
                    <div className="upload-album-actions">
                      <button 
                        onClick={createCustomAlbum} 
                        className="upload-create-btn"
                        disabled={!customAlbumName.trim()}
                      >
                        <Plus size={16} />
                        Create Album
                      </button>
                      <button 
                        onClick={() => {
                          setShowCreateAlbum(false);
                          setCustomAlbumName('');
                        }}
                        className="upload-cancel-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Upload Section - Only show if album is selected and not at limit */}
            {selectedAlbum && getRemainingSlots() > 0 && consentAccepted && (
              <section className="upload-content-section">
                <div className="upload-section-header">
                  <UploadIcon size={20}  />
                  <h2 className="upload-section-title">Upload Photos to "{selectedAlbum}"</h2>
                </div>
                
                {/* File Upload Area */}
                <div 
                  className={`upload-drop-zone ${dragActive ? 'drag-active' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="upload-drop-content">
                    <ImageIcon size={48}  />
                    <h3>Drag & Drop Images Here</h3>
                    <p>or</p>
                    <label className="upload-file-label">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        disabled={uploading || getRemainingSlots() === 0}
                      />
                      <span className={`upload-browse-btn ${uploading || getRemainingSlots() === 0 ? 'disabled' : ''}`}>
                        Browse Files
                      </span>
                    </label>
                    <p className="upload-file-info">
                      Supports: JPG, PNG, GIF, WebP (Maximum size: 16 MB - optimized automatically)
                      <br />
                      <strong>Remaining slots: {getRemainingSlots()}/{MAX_TOTAL_PHOTOS}</strong>
                    </p>
                  </div>
                </div>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="upload-selected-files">
                    <div className="upload-files-header">
                      <h3>Selected Files ({selectedFiles.length})</h3>
                      <button onClick={clearAllFiles} className="upload-clear-btn" disabled={uploading}>
                        Clear All
                      </button>
                    </div>
                    
                    <div className="upload-files-list">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="upload-file-item">
                          <div className="upload-file-info">
                            <span className="upload-file-name">{file.name}</span>
                          </div>
                          <button 
                            onClick={() => removeSelectedFile(index)}
                            className="upload-remove-btn"
                            disabled={uploading}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="upload-actions">
                      <button 
                        onClick={uploadImages}
                        disabled={uploading}
                        className="upload-submit-btn"
                      >
                        {uploading ? (
                          <>
                            <div className="upload-spinner"></div>
                            Uploading to {selectedAlbum}...
                          </>
                        ) : (
                          <>
                            <UploadIcon size={16} />
                            Upload to {selectedAlbum}
                          </>
                        )}
                      </button>
                    </div>

                    {/* Upload Progress */}
                    {uploading && Object.keys(uploadProgress).length > 0 && (
                      <div className="upload-progress-section">
                        <h4>Upload Progress</h4>
                        {Object.entries(uploadProgress).map(([fileId, progress]) => (
                          <div key={fileId} className="upload-progress-item">
                            <div className="upload-progress-bar">
                              <div 
                                className="upload-progress-fill"
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <span className="upload-progress-text">{progress}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* Upload Consent Required Message */}
            {selectedAlbum && !consentAccepted && (
              <section className="upload-content-section">
                <div className="upload-consent-required">
                  <h3><AlertTriangle size={17} /> Consent required</h3>
                  <p>Please accept the upload terms above to start uploading photos.</p>
                </div>
              </section>
            )}

            {/* Upload Limit Reached Message */}
            {selectedAlbum && getRemainingSlots() === 0 && (
              <section className="upload-content-section">
                <div className="upload-limit-reached">
                  <h3><AlertTriangle size={17} /> Upload limit reached</h3>
                  <p>You've reached the maximum of {MAX_TOTAL_PHOTOS} photos. Please delete some photos to upload more.</p>
                </div>
              </section>
            )}

            {/* Your Albums Grid */}
            <section className="upload-content-section">
              <div className="upload-section-header">
                <FolderOpen size={20}  />
                <h2 className="upload-section-title">Your Albums</h2>
              </div>
              
              <div className="upload-folders-grid">
                {userAlbums.map((album) => {
                  const albumData = albumPhotos[album] || { count: 0, images: [], firstImage: null, lastUpdated: null };
                  const isDeletingThisAlbum = deletingAlbum === album;
                  const canDelete = album !== 'Portfolio';
                  
                  return (
                    <div 
                      key={album} 
                      className={`upload-folder-card ${isDeletingThisAlbum ? 'deleting' : ''}`}
                      onClick={() => !isDeletingThisAlbum && handleAlbumClick(album)}
                      style={{ cursor: isDeletingThisAlbum ? 'not-allowed' : 'pointer' }}
                    >
                      <div className="upload-folder-icon">
                        {albumData.firstImage ? (
                          <img
                            src={albumData.firstImage}
                            alt={`${album} preview`}
                            className="upload-album-preview-image"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <Camera size={50} className="upload-folder-image upload-folder-image--pink" />
                        )}
                      </div>
                      <div className="upload-folder-info">
                        <div className="upload-folder-name">
                          <Camera size={16}  />
                          <span>{album}</span>
                        </div>
                        <div className="upload-file-count">
                          <ImageIcon size={12} /> {albumData.count} photos
                        </div>
                        <div className="upload-last-edit">
                          <Clock size={12} /> {formatDate(albumData.lastUpdated)}
                        </div>
                      </div>
                      
                      {/* ✨ UPDATED: Only Delete Button - positioned absolutely */}
                      {canDelete && (
                        <button 
                          className="upload-delete-album-button"
                          onClick={(event) => handleDeleteAlbum(album, event)}
                          disabled={isDeletingThisAlbum}
                          title={`Delete ${album} album`}
                        >
                          {isDeletingThisAlbum ? (
                            <div className="upload-spinner"></div>
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        ) : (
          /* Album View */
          <section className="upload-content-section">
            <div className="upload-section-header">
              <button 
                onClick={() => setViewingAlbum(null)}
                className="upload-back-btn"
              >
                ← Back
              </button>
              <h2 className="upload-section-title">{viewingAlbum} Photos ({albumImages.length})</h2>
            </div>
            
            <div className="upload-album-images-grid">
              {albumImages.length > 0 ? (
                albumImages.map((image, index) => (
                  <div key={index} className="upload-album-image-item">
                    <img
                      src={image.url}
                      alt={image.fileName}
                      className="upload-album-image"
                      loading="lazy"
                      decoding="async"
                      onClick={() => openImageModal(image)}
                    />
                    <div className="upload-image-overlay">
                      <button 
                        className="upload-image-action-btn"
                        onClick={() => openImageModal(image)}
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className="upload-image-action-btn upload-delete-btn"
                        onClick={() => deleteImage(image.url, viewingAlbum)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="upload-image-info">
                      <span className="upload-image-name">{image.fileName}</span>
                      <span className="upload-image-date">
                        {formatDate(image.uploadedAt)}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="upload-no-images">
                  <ImageIcon size={48}  />
                  <p>No photos in this album yet</p>
                  <button 
                    onClick={() => {
                      setViewingAlbum(null);
                      setSelectedAlbum(viewingAlbum);
                    }}
                    className="upload-add-photos-btn"
                  >
                    Add Photos
                  </button>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {/* ✨ Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="upload-delete-modal-overlay" onClick={cancelDeleteAlbum}>
          <div className="upload-delete-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="upload-delete-modal-header">
              <h3><AlertTriangle size={18} /> Delete album</h3>
              <button className="upload-modal-close" onClick={cancelDeleteAlbum}>
                <X size={18} />
              </button>
            </div>

            <div className="upload-delete-modal-body">
              <p>Are you sure you want to delete the album <strong>"{albumToDelete}"</strong>?</p>
              <div className="upload-delete-warning">
                <p>This will permanently delete:</p>
                <ul>
                  <li>All {albumPhotos[albumToDelete]?.count || 0} photos in this album</li>
                  <li>Album data and metadata</li>
                </ul>
                <p><strong>This action cannot be undone.</strong></p>
              </div>
            </div>
            
            <div className="upload-delete-modal-actions">
              <button 
                className="upload-cancel-delete-btn"
                onClick={cancelDeleteAlbum}
              >
                Cancel
              </button>
              <button 
                className="upload-confirm-delete-btn"
                onClick={confirmDeleteAlbum}
              >
                <Trash2 size={16} />
                Delete Album
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="upload-image-modal" onClick={closeImageModal}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="upload-modal-close" onClick={closeImageModal}>
              <X size={24} />
            </button>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.fileName}
              className="upload-modal-image"
            />
            <div className="upload-modal-info">
              <h3>{selectedImage.fileName}</h3>
              <p>Uploaded: {formatDate(selectedImage.uploadedAt)}</p>
            </div>
          </div>
        </div>
      )}
      
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Upload;
